import React, { useEffect, useState } from 'react';
import { DocumentViewer } from '../../../..';
import { IEmailDetailProps } from './email-detail.types';
import * as S from '../claim-timeline.styles';
import ContentLoader from 'react-content-loader';

interface IDocumentFields {
    id?: string;
    description?: string;
    documentType?: string;
    filename?: string;
}

const LinePlaceholder = React.memo(() => (
    <ContentLoader speed={2} height={20} style={{ width: '100%' }} preserveAspectRatio="">
        <rect x="0" y="0" rx="0" ry="0" width="50%" height="20" />
    </ContentLoader>
));

function formatAddress(name?: string, email?: string): string {
    if (name && email) return `${name} <${email}>`;
    return email ?? '';
}

export const EmailDetail: React.FunctionComponent<IEmailDetailProps> = (props): React.ReactElement => {
    const { email, appType, downloadDocument } = props;
    const [loading, setLoading] = useState(false);
    const [documentUrl, setDocumentUrl] = useState<{ url?: string } | undefined>();
    const [viewFile, setViewFile] = useState(false);

    const document = email.document as IDocumentFields | undefined;
    const documentId = document?.id;

    useEffect(() => {
        if (appType !== 'support' || !documentId || !downloadDocument || !viewFile) return;
        let cancelled = false;
        setLoading(true);
        downloadDocument(documentId)
            .then((url) => {
                if (!cancelled) setDocumentUrl(url);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [appType, documentId, downloadDocument, viewFile]);

    if (appType === 'provider') {
        return (
            <S.ExpandWrap key={email.id}>
                <S.ExpandContainer>
                    <S.ExpandTable>
                        <S.ExpandTBody>
                            <S.ExpandTR>
                                <S.ExpandTH>From</S.ExpandTH>
                                <S.ExpandTD>{formatAddress(email.fromName, email.fromEmail)}</S.ExpandTD>
                            </S.ExpandTR>
                            <S.ExpandTR>
                                <S.ExpandTH>To</S.ExpandTH>
                                <S.ExpandTD>{formatAddress(email.toName, email.toEmail)}</S.ExpandTD>
                            </S.ExpandTR>
                            <S.ExpandTR>
                                <S.ExpandTH>Subject</S.ExpandTH>
                                <S.ExpandTD>{email.subject}</S.ExpandTD>
                            </S.ExpandTR>
                        </S.ExpandTBody>
                    </S.ExpandTable>
                </S.ExpandContainer>
            </S.ExpandWrap>
        );
    }

    const fromDescription = formatAddress(email.fromName, email.fromEmail);
    const toDescription = formatAddress(email.toName, email.toEmail);

    return (
        <S.ExpandWrap key={email.id}>
            <S.ExpandContainer>
                <S.ExpandTable>
                    <S.ExpandTBody>
                        <S.ExpandTR>
                            <S.ExpandTH>ID</S.ExpandTH>
                            <S.ExpandTD>{email.id}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>From</S.ExpandTH>
                            <S.ExpandTD>{fromDescription}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>To</S.ExpandTH>
                            <S.ExpandTD>{toDescription}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Subject</S.ExpandTH>
                            <S.ExpandTD>{email.subject}</S.ExpandTD>
                        </S.ExpandTR>
                    </S.ExpandTBody>
                </S.ExpandTable>
                {document && (
                    <>
                        {!viewFile ? (
                            <S.ExpandDetailButton type="button" onClick={() => setViewFile(true)}>
                                View Document
                            </S.ExpandDetailButton>
                        ) : (
                            <>
                                <S.ExpandTable>
                                    <S.ExpandTBody>
                                        <S.ExpandTR>
                                            <S.ExpandTH>Document ID</S.ExpandTH>
                                            <S.ExpandTD>{document.id}</S.ExpandTD>
                                        </S.ExpandTR>
                                        <S.ExpandTR>
                                            <S.ExpandTH>Description</S.ExpandTH>
                                            <S.ExpandTD>{document.description}</S.ExpandTD>
                                        </S.ExpandTR>
                                        <S.ExpandTR>
                                            <S.ExpandTH>Type</S.ExpandTH>
                                            <S.ExpandTD>{document.documentType}</S.ExpandTD>
                                        </S.ExpandTR>
                                        <S.ExpandTR>
                                            <S.ExpandTH>Filename</S.ExpandTH>
                                            <S.ExpandTD>{document.filename}</S.ExpandTD>
                                        </S.ExpandTR>
                                    </S.ExpandTBody>
                                </S.ExpandTable>
                                {loading && <LinePlaceholder />}
                                {documentUrl?.url && <DocumentViewer url={documentUrl.url} />}
                            </>
                        )}
                    </>
                )}
                {email.contents && (
                    <S.ExpandIframe title="Email contents" srcDoc={email.contents} />
                )}
            </S.ExpandContainer>
        </S.ExpandWrap>
    );
};
